import { useInfiniteQuery } from '@tanstack/react-query';
import { useApiQuery } from '../useApiQuery';
import { contactKeys } from '@/lib/queryKeys';
import { apiGet, operationsClient } from '@/lib/api';
import type { Contact } from '@/types/contact';
import { normalizeContact } from './useContacts';

const RELATIONSHIP_PAGE_SIZE = 100;

type ContactListWireResponse = {
  data?: Record<string, unknown>[];
  items?: Record<string, unknown>[];
  total?: number;
};

export type ContactRelationships = {
  accountId: string;
  parent: Contact | null;
  children: Contact[];
};

export function getAccountContactId(contact: Pick<Contact, 'id' | 'parentContactId'>): string {
  return contact.parentContactId || contact.id;
}

async function fetchChildrenPage(
  accountId: string,
  page: number,
  search?: string,
): Promise<ContactListWireResponse> {
  return apiGet<ContactListWireResponse>(operationsClient, '/contacts', {
    params: {
      parentContactId: accountId,
      page,
      limit: RELATIONSHIP_PAGE_SIZE,
      sortBy: 'displayName',
      sortDir: 'asc',
      ...(search ? { search } : {}),
    },
  });
}

function recordsFromPage(page: ContactListWireResponse): Record<string, unknown>[] {
  return page.data ?? page.items ?? [];
}

export async function fetchContactChildrenPage(accountId: string, page: number, search?: string) {
  const response = await fetchChildrenPage(accountId, page, search);
  const records = recordsFromPage(response);
  return {
    children: records.map(normalizeContact),
    total: Number(response.total ?? records.length),
    page,
  };
}

/**
 * Paginated child-contact query for company/location pickers. Only the first
 * page is requested when the location step opens; further pages are fetched
 * explicitly so large commercial accounts do not block create forms.
 */
export function useContactChildren(accountId: string, search = '') {
  const normalizedSearch = search.trim();
  return useInfiniteQuery({
    queryKey: [
      ...contactKeys.relationships(accountId, accountId),
      'paged-children',
      normalizedSearch,
    ],
    queryFn: ({ pageParam }) =>
      fetchContactChildrenPage(accountId, pageParam, normalizedSearch || undefined),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page * RELATIONSHIP_PAGE_SIZE < lastPage.total ? lastPage.page + 1 : undefined,
    enabled: Boolean(accountId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Loads the entire account relationship in one non-polling query. The general
 * contact-list hook is intentionally not used here: it polls and exposes only
 * one page, which made relationship cards both noisy and incomplete for
 * accounts with more than 100 linked contacts.
 */
export async function fetchContactRelationships(
  contact: Pick<Contact, 'id' | 'parentContactId'>,
): Promise<ContactRelationships> {
  const accountId = getAccountContactId(contact);
  const firstPage = await fetchChildrenPage(accountId, 1);

  const firstRecords = recordsFromPage(firstPage);
  const total = Number(firstPage.total ?? firstRecords.length);
  const pageCount = Math.max(1, Math.ceil(total / RELATIONSHIP_PAGE_SIZE));
  const remainingPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) => fetchChildrenPage(accountId, index + 2)),
  );
  const childRecords = [
    ...firstRecords,
    ...remainingPages.flatMap((page) => recordsFromPage(page)),
  ];
  // Each child row's parent projection is produced by the scope-filtered
  // contact-list query. Reuse it instead of making a separate parent detail
  // request, which could drift from list visibility rules.
  const viewedChild = contact.parentContactId
    ? childRecords.find((record) => record.id === contact.id)
    : undefined;
  const parentRaw = viewedChild?.parentContact;

  return {
    accountId,
    parent:
      parentRaw && typeof parentRaw === 'object'
        ? normalizeContact(parentRaw as Record<string, unknown>)
        : null,
    children: childRecords.map(normalizeContact),
  };
}

export function useContactRelationships(contact: Pick<Contact, 'id' | 'parentContactId'>) {
  const accountId = getAccountContactId(contact);
  return useApiQuery<ContactRelationships>({
    // Account and child views share an accountId but intentionally differ in
    // `parent`. Include the viewed contact so a fresh company-view cache entry
    // cannot be reused on a child page (or vice versa).
    queryKey: contactKeys.relationships(accountId, contact.id),
    queryFn: () => fetchContactRelationships(contact),
    enabled: Boolean(accountId),
    showErrorToast: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
